import React from 'react';
import { setReturn } from '../operations/ReturnToList.jsx';
const { useState, useEffect, useRef } = React;
import { inputSt, textareaSt, labelSt, cardSt, mutedSt, warnSt, fmtDate } from '../diagnostic/shared.jsx';
import { coachCall } from './shared.jsx';
import {
  LESSON_KINDS, RESPONSE_TYPES, LEARNING_LIMITS as LL, FILE_FORMATS_TEXT, VIDEO_PROVIDERS_TEXT, COVER_STYLES, COVER_TONES, DEFAULT_COVER,
  learningLabel, newLearningId, videoEmbedOf, lessonHasTask, fileTypeOf,
} from '../../learningConfig.js';
import { readFileForUpload, saveDataUrl, fmtSize, LessonText, CourseCover } from '../learning/learningParts.jsx';
import CatalogEditor from '../learning/CatalogEditor.jsx';
import RulesEditor from '../learning/RulesEditor.jsx';

// Acompañamiento › Aprendizaje: the coach's review inbox, course cards and the
// people learning. The course is the original; each assignment keeps a copy of
// the version it got (shared/learning.ts). People's drafts never come here:
// only what they handed in. Nothing is sent by email.
// One primary action per place (Revisar, Editar, Publicar, Enviar devolución);
// the rest are secondary.

const FILE_ERRORS = { file_type: `Ese formato no se acepta: ${FILE_FORMATS_TEXT}.`, file_too_large: 'El archivo pasa de 4 MB.', unreadable: 'No se pudo leer el archivo.' };
const small = { fontSize: 12, padding: '4px 10px' };
const ghost = { ...small, background: 'transparent', border: '1px solid var(--globant-border)' };
const linkBtn = { background: 'none', border: 0, padding: 0, font: 'inherit', fontSize: 13, color: 'var(--globant-green)', fontWeight: 600, cursor: 'pointer', textDecoration: 'underline' };
const box = { border: '1px solid var(--globant-border)', borderRadius: 14, padding: 14, background: 'var(--globant-card)' };
const h2St = { fontSize: 17, margin: '0 0 10px' };
const pill = (tone) => ({
  display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, fontWeight: 700, padding: '2px 9px', borderRadius: 20, whiteSpace: 'nowrap',
  ...(tone === 'ok' ? { background: 'var(--teal-dim)', color: '#155C54' } : tone === 'warn' ? { background: '#FBF0DC', color: '#8A5A12' } : tone === 'info' ? { background: '#E5EEF8', color: '#2D5F99' } : { background: 'var(--globant-surface)', color: 'var(--globant-muted)' }),
});
const initials = (name) => String(name || '?').split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();
function Avatar({ name }) {
  return <span aria-hidden="true" style={{ flex: '0 0 36px', width: 36, height: 36, borderRadius: '50%', display: 'inline-grid', placeItems: 'center', background: 'var(--teal-dim)', color: '#155C54', fontWeight: 800, fontSize: 13 }}>{initials(name)}</span>;
}
const ago = (iso) => {
  if (!iso) return '';
  const h = Math.round((Date.now() - new Date(iso).getTime()) / 36e5);
  return h < 1 ? 'hace un momento' : h < 24 ? `hace ${h} h` : `hace ${Math.round(h / 24)} d`;
};

function Progress({ done, total, width = 90 }) {
  const pct = total ? Math.round((done / total) * 100) : 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      <span role="progressbar" aria-label="Pasos hechos" aria-valuemin={0} aria-valuemax={total} aria-valuenow={done} style={{ width, height: 8, borderRadius: 6, background: 'var(--globant-surface)', overflow: 'hidden', display: 'inline-block' }}>
        <span style={{ display: 'block', height: '100%', width: `${pct}%`, background: 'var(--globant-green)', borderRadius: 6 }} />
      </span>
      <span style={mutedSt}>{done} de {total}</span>
    </span>
  );
}

async function openFile(fileId, setMsg) {
  try {
    const r = await coachCall({ action: 'learningFile', fileId });
    if (!saveDataUrl(r.dataUrl, r.file.name)) setMsg(`En la demo no se descargan archivos: «${r.file.name}» (${fmtSize(r.file.size)}) se leyó por el servidor con permiso.`);
  } catch (e) { setMsg(e.message); }
}

// ── Reviewing one assignment ──

/** One decision and its feedback, sent with a single primary button. */
function ReviewBox({ s, onDone }) {
  const [decision, setDecision] = useState('completed');
  const [feedback, setFeedback] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const send = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try { await coachCall({ action: 'learningReview', submissionId: s.id, decision, feedback }); onDone(); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ marginTop: 12, padding: 14, borderRadius: 12, background: 'var(--globant-surface)' }}>
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Tu devolución para la entrega {s.attempt}</div>
      <div role="radiogroup" aria-label="Decisión" style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
        {[['completed', '✓ Completado'], ['changes_requested', 'Pedir ajustes']].map(([k, l]) => (
          <label key={k} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 13, fontWeight: 600, border: `1px solid ${decision === k ? 'var(--globant-green)' : 'var(--globant-border)'}`, background: decision === k ? 'var(--teal-dim)' : 'var(--globant-card)' }}>
            <input type="radio" name={`d-${s.id}`} checked={decision === k} onChange={() => setDecision(k)} /> {l}
          </label>
        ))}
      </div>
      <textarea style={{ ...textareaSt, minHeight: 90 }} maxLength={LL.feedback} value={feedback} onChange={e => setFeedback(e.target.value)} aria-label="Devolución"
        placeholder={decision === 'changes_requested' ? 'Qué está bien y qué ajustar (obligatorio).' : 'Qué está bien (opcional).'} />
      {error && <div style={{ ...mutedSt, color: 'var(--globant-warning)' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
        <button className="action-btn btn-primary" disabled={busy || (decision === 'changes_requested' && !feedback.trim())} onClick={send}>Enviar devolución</button>
        <span style={mutedSt}>La persona la ve en su espacio. No se envía ningún correo.</span>
      </div>
    </div>
  );
}

export function EnrollmentView({ enrollmentId, focusLessonId = null, onBack }) {
  const [v, setV] = useState(null);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const focusRef = useRef(null);
  const load = () => coachCall({ action: 'learningEnrollment', enrollmentId }).then(setV).catch(e => setError(e.message));
  useEffect(() => { load(); }, [enrollmentId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (v && focusRef.current) focusRef.current.scrollIntoView({ block: 'start' }); }, [!!v]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error) return <div style={warnSt}>{error}</div>;
  if (!v) return <div style={mutedSt}>Cargando…</div>;
  const subsOf = (id) => v.submissions.filter(s => s.lessonId === id);
  const behind = v.course.latestVersion > v.enrollment.courseVersion;
  const update = async () => {
    if (!window.confirm(`¿Pasar esta asignación a la versión ${v.course.latestVersion}? Se conserva el progreso y las entregas de las lecciones que siguen; las de lecciones que ya no están quedan en el historial.`)) return;
    try { await coachCall({ action: 'learningUpdateVersion', enrollmentId, confirm: true }); load(); } catch (e) { setMsg(e.message); }
  };
  const withdraw = async () => {
    if (!window.confirm(`¿Retirar «${v.course.name}» a ${v.person.name}? Deja de verlo en su espacio. Sus entregas y tus devoluciones se conservan; no se borra nada.`)) return;
    try { await coachCall({ action: 'learningWithdraw', enrollmentId, confirm: true }); onBack(); } catch (e) { setMsg(e.message); }
  };
  const lessons = v.snapshot.modules.flatMap(m => m.lessons.map(l => ({ ...l, moduleTitle: m.title })));
  const withTask = lessons.filter(l => lessonHasTask(l));
  const STATE = { not_started: ['Por empezar', ''], in_progress: ['En curso', ''], viewed: ['Vista', 'ok'], draft: ['En curso', ''], submitted: ['Por revisar', 'info'], changes_requested: ['Ajustes pedidos', 'warn'], completed: ['Completada', 'ok'] };
  const Entry = ({ s }) => (
    <div style={{ border: '1px solid var(--globant-border)', borderRadius: 12, padding: 14, marginTop: 10, background: 'var(--globant-card)' }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <b>Entrega {s.attempt}</b><span style={mutedSt}>{fmtDate(s.submittedAt, true)}</span>
        {s.status === 'submitted' ? <span style={pill('info')}>Por revisar</span> : s.status === 'completed' ? <span style={pill('ok')}>Completada</span> : <span style={pill('warn')}>Ajustes pedidos</span>}
      </div>
      {s.text && <div style={{ whiteSpace: 'pre-wrap', margin: '8px 0 0', lineHeight: 1.6 }}>{s.text}</div>}
      {s.fileIds.map(id => <div key={id} style={{ marginTop: 6 }}><button style={linkBtn} onClick={() => openFile(id, setMsg)}>📎 {v.files[id]?.name || 'archivo'}</button> <span style={mutedSt}>{v.files[id] ? fmtSize(v.files[id].size) : ''}</span></div>)}
      {s.feedback && (
        <div style={{ margin: '10px 0 0 14px', padding: '10px 12px', borderRadius: 10, background: s.status === 'changes_requested' ? '#FBF0DC' : 'var(--teal-dim)', borderLeft: `3px solid ${s.status === 'changes_requested' ? '#8A5A12' : 'var(--globant-green)'}` }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: s.status === 'changes_requested' ? '#8A5A12' : '#155C54' }}>Tu devolución · {fmtDate(s.reviewedAt, true)}</div>
          <LessonText text={s.feedback} />
        </div>
      )}
      {s.status === 'submitted' && <ReviewBox s={s} onDone={load} />}
    </div>
  );
  return (
    <div>
      <button className="action-btn" style={ghost} onClick={onBack}>← Cursos</button>
      <div style={{ ...cardSt, marginTop: 10, display: 'flex', gap: 14, alignItems: 'center', flexWrap: 'wrap' }}>
        <Avatar name={v.person.name} />
        <div style={{ flex: '1 1 260px' }}>
          <h3 style={{ margin: 0 }}>{v.person.name} {v.person.id && <button type="button" className="action-btn" style={{ fontSize: 11, padding: '2px 8px', marginLeft: 6 }} onClick={() => { setReturn(`«${v.course.name}» de ${v.person.name}`, { page: 'coaching', section: 'learning', at: { enrollmentId } }, ['people']); window.dispatchEvent(new CustomEvent('oike:navigate', { detail: { page: 'coaching', personId: v.person.id } })); }}>Ficha de persona</button>}</h3>
          <div style={mutedSt}>{v.course.name} · versión {v.enrollment.courseVersion} · {v.process ? `proceso «${v.process.name}»` : 'asignación personal'}</div>
        </div>
        <Progress done={v.progress.done} total={v.progress.total} width={120} />
        {v.enrollment.status !== 'withdrawn' && <button style={{ ...linkBtn, color: 'var(--globant-danger)' }} onClick={withdraw}>Retirar asignación</button>}
      </div>
      {behind && <div style={{ ...box, marginBottom: 14, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}><span style={pill('warn')}>Versión {v.course.latestVersion} disponible</span><span style={{ fontSize: 13, flex: 1 }}>Esta asignación sigue en la versión {v.enrollment.courseVersion} hasta que la actualices.</span><button className="action-btn" style={ghost} onClick={update}>Actualizar a la versión {v.course.latestVersion}</button></div>}
      {msg && <div style={{ ...mutedSt, color: 'var(--globant-warning)', marginBottom: 10 }}>{msg}</div>}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 14, alignItems: 'start' }}>
        <aside style={{ ...box, gridColumn: 'span 1' }} aria-label="Recorrido">
          <div style={{ ...labelSt, marginBottom: 8 }}>Recorrido</div>
          {v.snapshot.modules.map(m => (
            <div key={m.id} style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--globant-muted)', margin: '6px 0' }}>{m.title}</div>
              {m.lessons.map(l => { const st = v.progress.states.find(x => x.id === l.id); const [lab, tone] = STATE[st?.state || 'not_started']; return (
                <div key={l.id} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, padding: '5px 0' }}><span style={{ flex: 1 }}>{l.title}</span><span style={pill(tone)}>{lab}</span></div>
              ); })}
            </div>
          ))}
          <div style={mutedSt}>Ves lo entregado; los borradores no. Ver un video no cuenta como paso hecho.</div>
        </aside>
        <div style={{ gridColumn: 'span 2', minWidth: 0 }}>
          {!withTask.length && <div style={box}>Este curso no tiene trabajos prácticos.</div>}
          {withTask.map(l => (
            <section key={l.id} ref={l.id === (focusLessonId || withTask.find(x => subsOf(x.id).some(s => s.status === 'submitted'))?.id) ? focusRef : undefined} style={{ ...box, marginBottom: 14 }} aria-label={l.title}>
              <div style={{ fontSize: 12, color: 'var(--globant-muted)' }}>{l.moduleTitle}</div>
              <h3 style={{ margin: '2px 0 6px' }}>✍️ {l.title}</h3>
              {l.instructions && <details><summary style={{ cursor: 'pointer', fontSize: 13 }}>Instrucciones del trabajo</summary><LessonText text={l.instructions} /></details>}
              {!subsOf(l.id).length ? <div style={{ ...mutedSt, marginTop: 8 }}>Todavía no entregó este trabajo.</div> : subsOf(l.id).map(s => <Entry key={s.id} s={s} />)}
            </section>
          ))}
          {v.removedWithSubmissions.length > 0 && (
            <section style={box}>
              <h3 style={{ marginTop: 0 }}>Entregas de lecciones que ya no están en esta versión</h3>
              {v.removedWithSubmissions.map(id => subsOf(id).map(s => <Entry key={s.id} s={s} />))}
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Course editor ──

const emptyLesson = (kind) => ({ id: newLearningId('l'), title: '', kind, body: '', videoUrl: '', fileIds: [], instructions: '', response: 'text', hasTask: false });

function CoverPicker({ cover, title, onChange }) {
  const c = { ...DEFAULT_COVER, ...(cover || {}) };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(160px, 220px) 1fr', gap: 14, alignItems: 'center' }}>
      <CourseCover cover={c} title={title} height={110} />
      <div>
        <div style={labelSt}>Portada</div>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }} role="radiogroup" aria-label="Dibujo">
          {COVER_STYLES.map(s => <button key={s.key} type="button" className="action-btn" aria-pressed={c.style === s.key} style={{ ...small, ...(c.style === s.key ? { background: 'var(--teal-dim)', borderColor: 'var(--globant-green)', color: '#155C54' } : {}) }} onClick={() => onChange({ style: s.key, tone: c.tone })}>{s.label}</button>)}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }} role="radiogroup" aria-label="Color">
          {COVER_TONES.map(t => <button key={t.key} type="button" title={t.label} aria-label={t.label} aria-pressed={c.tone === t.key} onClick={() => onChange({ style: c.style, tone: t.key })} style={{ width: 30, height: 30, borderRadius: '50%', cursor: 'pointer', background: `linear-gradient(135deg, ${t.light} 0 50%, ${t.deep} 50% 100%)`, border: c.tone === t.key ? '3px solid var(--globant-text)' : '1px solid var(--globant-border)' }} />)}
        </div>
        <div style={{ ...mutedSt, marginTop: 6 }}>Se dibuja con los colores de Oike.</div>
      </div>
    </div>
  );
}

function LessonEditor({ l, files, courseId, onChange, onRemove, onMove, first, last, onUploaded }) {
  const set = (p) => onChange({ ...l, ...p });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const embed = l.kind === 'video' && l.videoUrl ? videoEmbedOf(l.videoUrl) : null;
  const hasTask = lessonHasTask(l);
  const upload = async (e) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    setError(''); setBusy(true);
    try {
      const f = await readFileForUpload(file);
      const r = await coachCall({ action: 'learningUploadFile', courseId, name: f.name, data: f.data });
      onUploaded(r.file); set({ fileIds: [...l.fileIds, r.file.id] });
    } catch (err) { setError(FILE_ERRORS[err.code] || err.message); }
    setBusy(false);
  };
  return (
    <div style={{ border: '1px solid var(--globant-border)', borderRadius: 12, padding: 12, margin: '10px 0', background: 'var(--globant-card)' }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={pill('')}>{learningLabel(LESSON_KINDS, l.kind)}</span>
        <input style={{ ...inputSt, flex: '1 1 220px', width: 'auto' }} value={l.title} maxLength={LL.title} placeholder="Título de la lección" onChange={e => set({ title: e.target.value })} aria-label="Título de la lección" />
        <button className="action-btn" style={ghost} disabled={first} onClick={() => onMove(-1)} aria-label="Subir">↑</button>
        <button className="action-btn" style={ghost} disabled={last} onClick={() => onMove(1)} aria-label="Bajar">↓</button>
        <button className="action-btn" style={ghost} onClick={onRemove}>Quitar</button>
      </div>
      {l.kind === 'video' && <>
        <input style={{ ...inputSt, marginTop: 8 }} value={l.videoUrl} maxLength={LL.url} placeholder={`Enlace de ${VIDEO_PROVIDERS_TEXT}`} onChange={e => set({ videoUrl: e.target.value })} aria-label="Enlace del video" />
        <div style={mutedSt}>{!l.videoUrl ? `Se reproduce dentro de Oike con ${VIDEO_PROVIDERS_TEXT}.` : embed ? `✓ Se reproduce dentro de Oike (${embed.label}).` : 'Este enlace no es de un proveedor compatible: se abrirá en su sitio.'}</div>
      </>}
      {l.kind !== 'task' && <textarea style={{ ...textareaSt, marginTop: 8 }} value={l.body} maxLength={LL.body} placeholder={l.kind === 'text' ? 'Texto de la lectura' : 'Texto (opcional)'} onChange={e => set({ body: e.target.value })} aria-label="Texto" />}
      {(l.kind !== 'task') && (
        <label style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, marginTop: 8 }}><input type="checkbox" checked={hasTask} onChange={e => set({ hasTask: e.target.checked })} /> Agregar un trabajo práctico a esta lección</label>
      )}
      {hasTask && (
        <div style={{ marginTop: 8, padding: 10, borderRadius: 10, borderLeft: '3px solid var(--globant-green)', background: 'var(--teal-dim)' }}>
          <div style={{ ...labelSt, color: '#155C54' }}>Trabajo práctico</div>
          <textarea style={textareaSt} value={l.instructions} maxLength={LL.instructions} placeholder="Instrucciones del trabajo" onChange={e => set({ instructions: e.target.value })} aria-label="Instrucciones" />
          <select style={{ ...inputSt, width: 'auto', marginTop: 6 }} value={l.response} onChange={e => set({ response: e.target.value })} aria-label="Tipo de respuesta">{RESPONSE_TYPES.map(r => <option key={r.key} value={r.key}>{r.label}</option>)}</select>
        </div>
      )}
      <div style={{ marginTop: 8 }}>
        {l.fileIds.map(id => <div key={id} style={{ fontSize: 13, display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0' }}><span style={pill('info')}>{fileTypeOf(files[id]?.name)?.ext || 'archivo'}</span> {files[id]?.name || 'archivo'} <span style={mutedSt}>{files[id] ? fmtSize(files[id].size) : ''}</span><button className="action-btn" style={ghost} onClick={() => set({ fileIds: l.fileIds.filter(x => x !== id) })}>Quitar</button></div>)}
        {courseId && l.fileIds.length < LL.filesPerLesson && <label style={{ ...mutedSt, display: 'inline-flex', gap: 6, alignItems: 'center', cursor: 'pointer' }}>📎 Agregar material <input type="file" onChange={upload} disabled={busy} style={{ fontSize: 12 }} aria-label="Agregar material" /> {busy ? 'Subiendo…' : ''}</label>}
        {error && <div style={{ ...mutedSt, color: 'var(--globant-warning)' }}>{error}</div>}
      </div>
    </div>
  );
}

function CourseEditor({ courseId, people, processes, memberships, onBack, onSaved, startAssign = false }) {
  const [c, setC] = useState(null);
  const [form, setForm] = useState(null);
  const [files, setFiles] = useState({});
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [assign, setAssign] = useState(startAssign);
  const load = (id) => coachCall({ action: 'learningCourse', id }).then(r => {
    setC(r); setForm({ name: r.course.name, description: r.course.description, content: r.course.draft });
    setFiles(Object.fromEntries(r.files.map(f => [f.id, f])));
  }).catch(e => setError(e.message));
  useEffect(() => { if (courseId) load(courseId); else setForm({ name: '', description: '', content: { modules: [], cover: DEFAULT_COVER } }); }, [courseId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (error && !form) return <div style={warnSt}>{error}</div>;
  if (!form) return <div style={mutedSt}>Cargando…</div>;
  const mods = form.content.modules;
  const setMods = (next) => setForm({ ...form, content: { ...form.content, modules: next } });
  const setLesson = (mi, li, l) => setMods(mods.map((m, i) => (i !== mi ? m : { ...m, lessons: m.lessons.map((x, j) => (j === li ? l : x)) })));
  const save = async () => {
    if (busy) return null;
    setBusy(true); setError(''); setMsg('');
    try {
      const r = await coachCall({ action: 'learningSaveCourse', id: c?.course.id, course: form });
      setC(r); setForm({ name: r.course.name, description: r.course.description, content: r.course.draft });
      setFiles(Object.fromEntries(r.files.map(f => [f.id, f]))); onSaved?.(); setMsg('Borrador guardado. Lo asignado no cambia.');
      setBusy(false); return r;
    } catch (e) { setError(e.message); setBusy(false); return null; }
  };
  const publish = async () => {
    const saved = await save(); if (!saved) return;
    if (!window.confirm(`¿Publicar la versión ${saved.course.version + 1}? Las asignaciones existentes siguen en su versión hasta que las actualices.`)) return;
    setBusy(true);
    try { const r = await coachCall({ action: 'learningPublish', id: saved.course.id, note }); setC(r); setNote(''); onSaved?.(); setMsg(`Publicada la versión ${r.course.version}.`); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const archive = async (archived) => {
    if (busy || !course) return;
    if (archived && !window.confirm(`¿Archivar «${course.name}»? Ya no se puede asignar. Las personas que lo tienen lo siguen viendo y sus entregas se conservan; no se borra nada.`)) return;
    setBusy(true); setError('');
    try { const r = await coachCall({ action: 'learningArchive', id: course.id, archived }); setC(r); onSaved?.(); setMsg(archived ? 'Curso archivado.' : 'Curso reactivado.'); } catch (e) { setError(e.message); }
    setBusy(false);
  };
  const course = c?.course;
  const dirty = !course || form.name !== course.name || form.description !== course.description || JSON.stringify(form.content) !== JSON.stringify(course.draft);
  const nothingNew = course?.version > 0 && !course.unpublishedChanges && !dirty;
  return (
    <div>
      <button className="action-btn" style={ghost} onClick={onBack}>← Cursos</button>
      <div style={{ ...cardSt, marginTop: 10 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, flex: 1 }}>{course ? course.name : 'Curso nuevo'}</h3>
          {course && <span style={pill(course.status === 'archived' ? '' : course.version ? 'ok' : '')}>{course.status === 'archived' ? 'Archivado' : course.version ? `Versión ${course.version} publicada` : 'Sin publicar'}</span>}
          {course?.version > 0 && (course.unpublishedChanges || dirty) && <span style={pill('warn')}>Cambios sin publicar</span>}
        </div>
        <CoverPicker cover={form.content.cover} title={form.name} onChange={(cover) => setForm({ ...form, content: { ...form.content, cover } })} />
        <label style={{ ...labelSt, marginTop: 14 }}>Nombre</label>
        <input style={inputSt} value={form.name} maxLength={LL.title} onChange={e => setForm({ ...form, name: e.target.value })} aria-label="Nombre del curso" />
        <label style={{ ...labelSt, marginTop: 10 }}>Descripción breve</label>
        <textarea style={textareaSt} value={form.description} maxLength={LL.description} onChange={e => setForm({ ...form, description: e.target.value })} aria-label="Descripción" />
        {c && <div style={{ ...mutedSt, marginTop: 6 }}>Asignado a {c.assigned} {c.assigned === 1 ? 'persona' : 'personas'}{c.behind ? ` · ${c.behind} en una versión anterior` : ''}.</div>}
      </div>
      {course && <CatalogEditor course={course} onSaved={(r) => { setC(r); onSaved?.(); }} />}
      {mods.map((m, mi) => (
        <div key={m.id} style={cardSt}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <span style={labelSt}>Módulo {mi + 1}</span>
            <input style={{ ...inputSt, flex: '1 1 220px', width: 'auto', fontWeight: 600 }} value={m.title} maxLength={LL.title} onChange={e => setMods(mods.map((x, i) => (i === mi ? { ...x, title: e.target.value } : x)))} aria-label={`Título del módulo ${mi + 1}`} />
            <button className="action-btn" style={ghost} onClick={() => window.confirm('¿Quitar el módulo y sus lecciones del borrador?') && setMods(mods.filter((_, i) => i !== mi))}>Quitar módulo</button>
          </div>
          {m.lessons.map((l, li) => (
            <LessonEditor key={l.id} l={l} files={files} courseId={course?.id} first={li === 0} last={li === m.lessons.length - 1}
              onChange={(x) => setLesson(mi, li, x)} onUploaded={(f) => setFiles({ ...files, [f.id]: f })}
              onRemove={() => setMods(mods.map((x, i) => (i === mi ? { ...x, lessons: x.lessons.filter((_, j) => j !== li) } : x)))}
              onMove={(d) => setMods(mods.map((x, i) => { if (i !== mi) return x; const ls = [...x.lessons]; [ls[li], ls[li + d]] = [ls[li + d], ls[li]]; return { ...x, lessons: ls }; }))} />
          ))}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
            <span style={mutedSt}>Agregar lección:</span>
            {LESSON_KINDS.map(k => <button key={k.key} className="action-btn" style={ghost} title={k.hint} onClick={() => setMods(mods.map((x, i) => (i === mi ? { ...x, lessons: [...x.lessons, emptyLesson(k.key)] } : x)))}>+ {k.label}</button>)}
          </div>
        </div>
      ))}
      <button className="action-btn" style={ghost} onClick={() => setMods([...mods, { id: newLearningId('m'), title: `Módulo ${mods.length + 1}`, lessons: [] }])} disabled={mods.length >= LL.modules}>+ Módulo</button>
      {!course && <div style={{ ...mutedSt, marginTop: 6 }}>Guarda el curso para agregar materiales.</div>}
      <div style={{ ...cardSt, marginTop: 14, position: 'sticky', bottom: 8, boxShadow: '0 6px 24px rgba(30,42,44,0.08)' }}>
        {error && <div style={{ ...mutedSt, color: 'var(--globant-warning)' }}>{error}</div>}
        {msg && <div style={{ ...mutedSt, color: 'var(--globant-green)' }} role="status">{msg}</div>}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 4 }}>
          {course && <button className="action-btn btn-primary" disabled={busy || nothingNew || !form.name.trim()} onClick={publish}>{nothingNew ? 'Sin cambios para publicar' : `Publicar versión ${course.version + 1}`}</button>}
          <button className={`action-btn ${course ? '' : 'btn-primary'}`} disabled={busy || !form.name.trim()} onClick={save}>{course ? 'Guardar borrador' : 'Crear curso'}</button>
          {course && <input style={{ ...inputSt, flex: '1 1 200px', width: 'auto' }} value={note} maxLength={LL.note} placeholder="Nota de la versión (opcional)" onChange={e => setNote(e.target.value)} aria-label="Nota de la versión" />}
          {course?.version > 0 && course.status !== 'archived' && <button style={linkBtn} onClick={() => setAssign(!assign)}>Asignar versión {course.version}</button>}
          {course && (course.status === 'archived'
            ? <button style={linkBtn} disabled={busy} onClick={() => archive(false)}>Reactivar curso</button>
            : <button style={{ ...linkBtn, color: 'var(--globant-danger)', marginLeft: 'auto' }} disabled={busy} onClick={() => archive(true)}>Archivar curso</button>)}
        </div>
        <div style={{ ...mutedSt, marginTop: 6 }}>Editas un borrador; publicar crea una versión nueva. Cada asignación conserva su versión hasta que la actualices.</div>
        {assign && course?.version > 0 && <AssignPanel course={course} people={people} processes={processes} memberships={memberships} onDone={(m) => { setAssign(false); setMsg(m); onSaved?.(); }} />}
        {c?.course.history?.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ cursor: 'pointer', fontSize: 12 }}>Versiones publicadas ({c.course.history.length})</summary>
            {c.course.history.map(h => <div key={h.version} style={{ fontSize: 12, margin: '4px 0' }}>v{h.version} · {fmtDate(h.at, true)} · {h.lessons} lecciones{h.note ? ` · ${h.note}` : ''}</div>)}
          </details>
        )}
      </div>
    </div>
  );
}

function AssignPanel({ course, people, processes, memberships, onDone, onCancel }) {
  const [mode, setMode] = useState('people');
  const [chosen, setChosen] = useState([]);
  const [processId, setProcessId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const open = processes.filter(p => p.status !== 'closed');
  const inProcess = memberships.filter(m => m.processId === processId && m.status !== 'left').map(m => people.find(p => p.id === m.personId)).filter(Boolean);
  const list = mode === 'people' ? people : inProcess;
  const toggle = (id) => setChosen(chosen.includes(id) ? chosen.filter(x => x !== id) : [...chosen, id]);
  const go = async () => {
    if (busy) return;
    setBusy(true); setError('');
    try {
      const r = await coachCall(mode === 'people' ? { action: 'learningAssign', courseId: course.id, personIds: chosen } : { action: 'learningAssign', courseId: course.id, processId, memberPersonIds: chosen });
      const fresh = r.enrollments.filter(e => !e.already).length, already = r.enrollments.length - fresh;
      onDone(`Asignada la versión ${course.version} de «${course.name}» a ${fresh} ${fresh === 1 ? 'persona' : 'personas'}${already ? ` (${already} ya lo tenía${already === 1 ? '' : 'n'})` : ''}.`);
    } catch (e) { setError(e.message); }
    setBusy(false);
  };
  return (
    <div style={{ ...box, marginTop: 12 }} aria-label="Asignar curso">
      <div style={{ fontWeight: 700, marginBottom: 8 }}>Asignar «{course.name}» · versión {course.version}</div>
      <div role="radiogroup" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontSize: 13 }}>
        <label><input type="radio" checked={mode === 'people'} onChange={() => { setMode('people'); setChosen([]); }} /> A personas</label>
        <label><input type="radio" checked={mode === 'process'} onChange={() => { setMode('process'); setChosen([]); }} /> A integrantes de un proceso</label>
      </div>
      {mode === 'process' && (
        <select style={{ ...inputSt, width: 'auto', marginTop: 8 }} value={processId} onChange={e => { setProcessId(e.target.value); setChosen([]); }} aria-label="Proceso">
          <option value="">Elegir proceso…</option>
          {open.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
      )}
      <div style={{ maxHeight: 190, overflow: 'auto', marginTop: 8, display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 4 }}>
        {list.map(p => <label key={p.id} style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: 13, padding: 4 }}><input type="checkbox" checked={chosen.includes(p.id)} onChange={() => toggle(p.id)} /> {p.name}</label>)}
      </div>
      {mode === 'process' && processId && !list.length && <div style={mutedSt}>Sin integrantes activos.</div>}
      <div style={{ ...mutedSt, marginTop: 6 }}>Cada persona tiene su propia asignación: sus entregas no se comparten con el equipo ni con quien contrata.{mode === 'people' ? ' Una asignación personal se ve con su link personal.' : ''}</div>
      {error && <div style={{ ...mutedSt, color: 'var(--globant-warning)' }}>{error}</div>}
      <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
        <button className="action-btn btn-primary" disabled={busy || !chosen.length || (mode === 'process' && !processId)} onClick={go}>Asignar a {chosen.length || '…'}</button>
        {onCancel && <button className="action-btn" onClick={onCancel}>Cancelar</button>}
      </div>
    </div>
  );
}

// ── The tab ──

function Tile({ value, label, tone, onClick }) {
  return (
    <button type="button" onClick={onClick} style={{ textAlign: 'left', font: 'inherit', cursor: onClick ? 'pointer' : 'default', flex: '1 1 160px', padding: '14px 16px', borderRadius: 14, border: `1px solid ${tone === 'hot' ? 'var(--teal-border)' : 'var(--globant-border)'}`, background: tone === 'hot' ? 'var(--teal-dim)' : 'var(--globant-card)', color: 'var(--globant-text)' }}>
      <div style={{ fontSize: 26, fontWeight: 800, color: tone === 'hot' ? '#155C54' : 'var(--globant-text)' }}>{value}</div>
      <div style={{ fontSize: 13, color: 'var(--globant-muted)' }}>{label}</div>
    </button>
  );
}

export default function LearningTab({ people = [], processes = [], memberships = [], initialEnrollment = null }) {
  const [o, setO] = useState(null);
  const [error, setError] = useState('');
  const [course, setCourse] = useState(null); // { id | 'new', assign }
  // Arriving at one submission (Personas, a team row or «Por atender»): that assignment and lesson, directly
  const [enrollment, setEnrollment] = useState(initialEnrollment?.enrollmentId ? { id: initialEnrollment.enrollmentId, lessonId: initialEnrollment.lessonId || null } : null); // { id, lessonId }
  const [assignFor, setAssignFor] = useState(null);
  const [filter, setFilter] = useState('');
  const [msg, setMsg] = useState('');
  const [rulesOpen, setRulesOpen] = useState(false);
  const peopleRef = useRef(null);
  const reviewRef = useRef(null);
  const load = () => coachCall({ action: 'learningOverview' }).then(x => { setO(x); setError(''); }).catch(e => setError(e.message));
  useEffect(() => { load(); }, []);
  if (error && !o) return <div style={warnSt}>{error}</div>;
  if (!o) return <div style={mutedSt}>Cargando…</div>;
  if (rulesOpen) return <RulesEditor onBack={() => { setRulesOpen(false); load(); }} />;
  if (enrollment) return <EnrollmentView enrollmentId={enrollment.id} focusLessonId={enrollment.lessonId} onBack={() => { setEnrollment(null); load(); }} />;
  if (course) return <CourseEditor courseId={course.id === 'new' ? null : course.id} startAssign={!!course.assign} people={people} processes={processes} memberships={memberships} onBack={() => { setCourse(null); load(); }} onSaved={load} />;
  const learners = new Set(o.enrollments.filter(e => e.status === 'active').map(e => e.personId)).size;
  const published = o.courses.filter(c => c.status === 'active').length;
  const shown = o.enrollments.filter(e => !filter || e.courseId === filter);
  const lessonOfReview = (r) => r.lessonId;
  return (
    <div>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap', marginBottom: 14 }}>
        <p style={{ ...mutedSt, margin: 0, flex: '1 1 320px', fontSize: 13 }}>Cursos con videos, lecturas, materiales y trabajos prácticos. Cada persona ve solo lo suyo en su espacio.</p>
        <button className="action-btn" onClick={() => setRulesOpen(true)}>Reglas de recomendación</button>
        <button className="action-btn btn-primary" onClick={() => setCourse({ id: 'new' })}>+ Nuevo curso</button>
      </div>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 18 }}>
        <Tile value={o.review.length} label={o.review.length === 1 ? 'trabajo por revisar' : 'trabajos por revisar'} tone={o.review.length ? 'hot' : ''} onClick={() => reviewRef.current?.scrollIntoView({ behavior: 'smooth' })} />
        <Tile value={learners} label={learners === 1 ? 'persona aprendiendo' : 'personas aprendiendo'} onClick={() => peopleRef.current?.scrollIntoView({ behavior: 'smooth' })} />
        <Tile value={published} label={published === 1 ? 'curso publicado' : 'cursos publicados'} />
      </div>
      {msg && <div style={{ ...box, marginBottom: 14, color: 'var(--globant-green)' }} role="status">{msg} <button style={linkBtn} onClick={() => setMsg('')}>Cerrar</button></div>}

      <section ref={reviewRef} aria-label="Por revisar" style={{ marginBottom: 22 }}>
        <h2 style={h2St}>Por revisar</h2>
        {!o.review.length ? <div style={{ ...box, ...mutedSt, fontSize: 13 }}>No hay trabajos esperando tu devolución.</div> : (
          <div style={{ display: 'grid', gap: 8 }}>
            {o.review.map(r => (
              <div key={r.id} style={{ ...box, display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '12px 14px' }}>
                <Avatar name={r.personName} />
                <div style={{ flex: '1 1 240px', minWidth: 0 }}>
                  <div style={{ fontWeight: 700 }}>{r.personName} <span style={{ fontWeight: 400 }}>· {r.lessonTitle}</span></div>
                  <div style={mutedSt}>{r.courseName} · entrega {r.attempt}{r.attempt > 1 ? ' (corregida)' : ''} · {ago(r.submittedAt)}</div>
                </div>
                <button className="action-btn btn-primary" onClick={() => setEnrollment({ id: r.enrollmentId, lessonId: lessonOfReview(r) })}>Revisar</button>
              </div>
            ))}
          </div>
        )}
      </section>

      <section aria-label="Cursos" style={{ marginBottom: 22 }}>
        <h2 style={h2St}>Cursos</h2>
        {!o.courses.length ? <div style={{ ...box, ...mutedSt }}>Todavía no hay cursos. Crea el primero con «+ Nuevo curso».</div> : (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: 14 }}>
            {o.courses.map(c => (
              <article key={c.id} style={{ ...box, padding: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }} aria-label={c.name}>
                <CourseCover cover={c.cover} title={c.name} height={110} rounded={0} />
                <div style={{ padding: 14, display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    <span style={pill(c.status === 'active' ? 'ok' : '')}>{c.status === 'archived' ? 'Archivado' : c.version ? `Publicado · v${c.version}` : 'Borrador'}</span>
                    {c.version > 0 && c.unpublishedChanges && <span style={pill('warn')}>Cambios sin publicar</span>}
                  </div>
                  <b style={{ fontSize: 15 }}>{c.name}</b>
                  <div style={mutedSt}>{c.modules} {c.modules === 1 ? 'módulo' : 'módulos'} · {c.lessons} lecciones</div>
                  <div style={{ fontSize: 13 }}>{c.assigned ? `${c.assigned} ${c.assigned === 1 ? 'persona' : 'personas'}` : 'Sin asignar'}{c.waiting ? <> · <span style={pill('info')}>{c.waiting} por revisar</span></> : null}{c.behind ? <span style={mutedSt}> · {c.behind} en versión anterior</span> : null}</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 'auto', paddingTop: 8 }}>
                    <button className="action-btn btn-primary" style={small} onClick={() => setCourse({ id: c.id })}>Editar</button>
                    {c.version > 0 && c.status === 'active' && <button className="action-btn" style={small} onClick={() => setAssignFor(assignFor?.id === c.id ? null : c)}>Asignar</button>}
                    {c.assigned > 0 && <button style={linkBtn} onClick={() => { setFilter(c.id); peopleRef.current?.scrollIntoView({ behavior: 'smooth' }); }}>Ver personas</button>}
                  </div>
                </div>
              </article>
            ))}
          </div>
        )}
        {assignFor && <AssignPanel course={assignFor} people={people} processes={processes} memberships={memberships} onCancel={() => setAssignFor(null)} onDone={(m) => { setAssignFor(null); setMsg(m); load(); }} />}
      </section>

      <section ref={peopleRef} aria-label="Personas aprendiendo">
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
          <h2 style={{ ...h2St, margin: 0, flex: 1 }}>Personas aprendiendo</h2>
          <select style={{ ...inputSt, width: 'auto' }} value={filter} onChange={e => setFilter(e.target.value)} aria-label="Filtrar por curso">
            <option value="">Todos los cursos</option>
            {o.courses.filter(c => c.assigned).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        {!shown.length ? <div style={{ ...box, ...mutedSt }}>Todavía no asignaste cursos.</div> : (
          <div style={{ ...box, padding: '4px 14px' }}>
            {shown.map(e => (
              <div key={e.id} style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', padding: '10px 0', borderBottom: '1px solid var(--globant-border)' }}>
                <Avatar name={e.personName} />
                <div style={{ flex: '1 1 200px', minWidth: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: 14 }}>{e.personName}</div>
                  <div style={mutedSt}>{e.courseName} · v{e.version}{e.processId ? ' · en un proceso' : ' · personal'}{e.lastActivityAt ? ` · ${ago(e.lastActivityAt)}` : ' · sin actividad'}</div>
                </div>
                <Progress done={e.done} total={e.total} />
                {e.status === 'completed' ? <span style={pill('ok')}>Terminado</span> : e.waiting > 0 ? <span style={pill('info')}>{e.waiting} por revisar</span> : null}
                {e.latestVersion > e.version && <span style={pill('warn')}>Hay v{e.latestVersion}</span>}
                <button className="action-btn" style={small} onClick={() => setEnrollment({ id: e.id })}>Ver</button>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
